import React, { useMemo, useState } from 'react';
import { 
    Autocomplete, 
    TextField, 
    FormControl, 
    FormHelperText, 
    Button 
} from '@mui/material';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isFunction } from '../../utils';
import InfoDialog from '../InfoDialog';
import { CompTooltipIcon } from '../TooltipIcon';

const SearchDropdownNew = (props) => {
    const { t } = useTranslation('translation');
    const {
        rows = [],
        label,
        onChange,
        labelColumn = 'label',
        valueColumn = 'value',
        selectedValue = null,
        className = '',
        disabled = false,
        name,
        userInfo = false,
        hasEmptyOption = false,
        isClearOption = false,
        ariaLabel,
    } = props;

    let { error } = props;
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const open = Boolean(null); 

    if (error !== undefined && error !== null) {
        error = t("errors." + error);
    } else {
        error = null;
    }

    const options = useMemo(() => {
        return (rows || [])
            .map((row) => ({
                id: row[valueColumn],
                label: row[labelColumn],
                originalData: row 
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [rows, labelColumn, valueColumn]);

    const currentValue = useMemo(() => {
        return options.find((opt) => opt.id === selectedValue) || null;
    }, [options, selectedValue]);

    const handleChange = (event, newValue) => {
        if (isFunction(onChange)) {
            onChange({
                target: {
                    name: name,
                    value: newValue ? newValue.id : ''
                }
            });
        }
    };

    const handleOpenDialog = () => setIsDialogOpen(true);
    const handleCloseDialog = () => setIsDialogOpen(false);

    return (
        <div className={`neutral-wrapper ${userInfo ? 'dropdown-wrapper' : ''}`}>
            <div style={{ display: 'flex' }}>
                {userInfo && (
                    <div className="tooltip-container">
                        <Button onClick={handleOpenDialog}>
                            <img className={`tooltip-icon-image ${open ? 'hidden' : ''}`}
                                src={process.env.PUBLIC_URL + '/media/images/icons/IconTooltipInfo.svg'}
                                alt={t('common.info')} />
                            <img className={`tooltip-icon-image ${open ? '' : 'hidden'}`}
                                src={process.env.PUBLIC_URL + '/media/images/icons/IconTooltipInfoFocus.svg'}
                                alt={t('common.info')} />
                        </Button>
                        <InfoDialog
                            open={isDialogOpen}
                            handleClose={handleCloseDialog}
                            title={t('user.userRoleTitle')}
                            message={t('user.userRoleInfo')}
                        />
                    </div>
                )}
            </div>

            <FormControl 
                fullWidth 
                className={`comp-dropdown-container ${className ? 'container-' + className : ''} ${error ? 'error' : ''}`} 
                disabled={disabled}
            >
                <Autocomplete
                    value={currentValue}
                    onChange={handleChange}
                    options={options}
                    disabled={disabled}
                    disableClearable={!hasEmptyOption && !isClearOption}
                    getOptionLabel={(option) => option.label || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    className={`dropdown_custom ${className}`}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id} className={`option ${props.className || ''}`}>
                            {option.label}
                            {option.originalData.descriptionFi && t('report.current_language') === "FINNISH" && (
                                <CompTooltipIcon ignore={true}>{option.originalData.descriptionFi}</CompTooltipIcon>
                            )}
                            {option.originalData.descriptionSv && t('report.current_language') !== "FINNISH" && (
                                <CompTooltipIcon ignore={true}>{option.originalData.descriptionSv}</CompTooltipIcon>
                            )}
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={label}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                ...params.inputProps,
                                'aria-label': ariaLabel
                            }}
                            sx={{
                                backgroundColor: "white",
                                '& .MuiOutlinedInput-root': {
                                    padding: "0px 39px 0px 0px !important",
                                    height: "39px !important",
                                    '& fieldset': {
                                        borderColor: '#404CE2',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: disabled ? '#00000042' : '#007FFF',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#404CE2',
                                    },
                                },
                                '& .MuiAutocomplete-input': {
                                    padding: '0px 14px !important',
                                }
                            }}
                        />
                    )}
                />
                <FormHelperText className={`error-label ${className ? 'error-' + className : ''}`}>
                    {error}
                </FormHelperText>
            </FormControl>
        </div>
    );
};

SearchDropdownNew.propTypes = {
    labelColumn: PropTypes.string,
    valueColumn: PropTypes.string,
    label: PropTypes.string,
    rows: PropTypes.array,
    error: PropTypes.string,
    hasEmptyOption: PropTypes.bool,
    isClearOption: PropTypes.bool,
    onChange: PropTypes.func,
    selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
    disabled: PropTypes.bool,
    name: PropTypes.string,
    userInfo: PropTypes.bool,
    ariaLabel: PropTypes.string,
};

export { SearchDropdownNew };